import { useCallback, useEffect, useRef, useState } from "react";
import {
  midiSupported,
  parseMidiMessage,
  midiToKey,
  mapMidiInput,
  playNote,
} from "../../lib/keyboardPlan";

/* ============================================================
   useMidi — bridges a real MIDI keyboard (Web MIDI API) into the
   same { name, octave } presses the on-screen Piano emits.

   On a note-on it sounds the note and calls onNoteOn(key) so a
   build question's `pressed` set fills in without touching the screen.

   Returns { status, devices }:
     status  : "unsupported" | "idle" | "denied" | "no-devices" | "connected"
     devices : number of attached MIDI inputs

   onNoteOn is read through a ref so the latest closure (current
   question state) is used without re-requesting MIDI access.
   ============================================================ */
export default function useMidi(onNoteOn, enabled = true) {
  const [status, setStatus] = useState(() =>
    midiSupported() ? "idle" : "unsupported"
  );
  const [devices, setDevices] = useState(0);
  const cbRef = useRef(onNoteOn);
  cbRef.current = onNoteOn;

  useEffect(() => {
    if (!enabled || !midiSupported()) return undefined;

    let access = null;
    let cancelled = false;

    const handleMessage = (event) => {
      const msg = parseMidiMessage(event.data);
      if (!msg || msg.type !== "on") return;
      const key = midiToKey(mapMidiInput(msg.midi));
      playNote(key.midi, 600);
      if (cbRef.current) cbRef.current(key);
    };

    // (Re)attach our listener to every input and report how many we see.
    const wireInputs = () => {
      if (!access) return;
      const inputs = Array.from(access.inputs.values());
      inputs.forEach((input) => {
        input.onmidimessage = handleMessage;
      });
      if (!cancelled) {
        setDevices(inputs.length);
        setStatus(inputs.length ? "connected" : "no-devices");
      }
    };

    navigator
      .requestMIDIAccess()
      .then((midiAccess) => {
        if (cancelled) return;
        access = midiAccess;
        access.onstatechange = wireInputs; // hot-plug devices
        wireInputs();
      })
      .catch(() => {
        if (!cancelled) setStatus("denied");
      });

    return () => {
      cancelled = true;
      if (access) {
        access.onstatechange = null;
        Array.from(access.inputs.values()).forEach((input) => {
          input.onmidimessage = null;
        });
      }
    };
  }, [enabled]);

  return { status, devices };
}

/* ============================================================
   useMidiHeld — tracks the set of MIDI notes currently held down
   (note-on adds, note-off removes), for drills that auto-advance the
   moment the right chord is under the hands — no "check" button. Sounds
   each note-on like useMidi. Returns { status, devices, held } where
   `held` is a Set of MIDI numbers that changes as keys go up and down.
   ============================================================ */
export function useMidiHeld(enabled = true) {
  const [status, setStatus] = useState(() =>
    midiSupported() ? "idle" : "unsupported"
  );
  const [devices, setDevices] = useState(0);
  const [held, setHeld] = useState(() => new Set());

  useEffect(() => {
    if (!enabled || !midiSupported()) return undefined;
    let access = null;
    let cancelled = false;

    const handleMessage = (event) => {
      const msg = parseMidiMessage(event.data);
      if (!msg) return;
      const midi = mapMidiInput(msg.midi);
      if (msg.type === "on") {
        playNote(midi, 500);
        setHeld((prev) => {
          const next = new Set(prev);
          next.add(midi);
          return next;
        });
      } else if (msg.type === "off") {
        setHeld((prev) => {
          if (!prev.has(midi)) return prev;
          const next = new Set(prev);
          next.delete(midi);
          return next;
        });
      }
    };

    const wireInputs = () => {
      if (!access) return;
      const inputs = Array.from(access.inputs.values());
      inputs.forEach((input) => {
        input.onmidimessage = handleMessage;
      });
      if (!cancelled) {
        setDevices(inputs.length);
        setStatus(inputs.length ? "connected" : "no-devices");
      }
    };

    navigator
      .requestMIDIAccess()
      .then((midiAccess) => {
        if (cancelled) return;
        access = midiAccess;
        access.onstatechange = wireInputs;
        wireInputs();
      })
      .catch(() => {
        if (!cancelled) setStatus("denied");
      });

    return () => {
      cancelled = true;
      if (access) {
        access.onstatechange = null;
        Array.from(access.inputs.values()).forEach((input) => {
          input.onmidimessage = null;
        });
      }
    };
  }, [enabled]);

  return { status, devices, held };
}

/* ============================================================
   useMidiPresence — a lightweight "is a MIDI keyboard plugged in?"
   signal for code that runs before any note input exists (e.g. the
   page deciding how to shape a lesson). It only reads input count and
   listens for hot-plug via addEventListener, and never touches
   input.onmidimessage / access.onstatechange — so it can't conflict
   with the note-input useMidi a lesson or game mounts later.
   ============================================================ */
/* ============================================================
   useMidiMonitor — the diagnostic behind the settings "MIDI setup"
   panel. It names the connected inputs and echoes every incoming note so
   a player can confirm each key on their keyboard registers: `held` is
   what's down right now, `seen` accumulates every distinct note pressed
   this session (press each key once to prove the whole board works), and
   `last` is the most recent press. Sounds each note-on like useMidi.
   `reset` clears the accumulated history. Only mounted on the settings
   screen, so it never competes with a lesson's note input.
   ============================================================ */
export function useMidiMonitor(enabled = true) {
  const [status, setStatus] = useState(() =>
    midiSupported() ? "idle" : "unsupported"
  );
  const [devices, setDevices] = useState(0);
  const [deviceNames, setDeviceNames] = useState([]);
  const [held, setHeld] = useState(() => new Set());
  const [seen, setSeen] = useState(() => new Set());
  const [last, setLast] = useState(null);

  const reset = useCallback(() => {
    setSeen(new Set());
    setLast(null);
  }, []);

  useEffect(() => {
    if (!enabled || !midiSupported()) return undefined;
    let access = null;
    let cancelled = false;

    const handleMessage = (event) => {
      const msg = parseMidiMessage(event.data);
      if (!msg) return;
      const midi = mapMidiInput(msg.midi);
      if (msg.type === "on") {
        // `raw` keeps the un-mapped note so the settings range-calibration can
        // capture the keyboard's true lowest/highest keys; everything shown
        // (held / seen / last) is mapped, like the lessons will see it.
        const key = { ...midiToKey(midi), raw: msg.midi };
        playNote(key.midi, 500);
        setLast(key);
        setHeld((prev) => new Set(prev).add(midi));
        setSeen((prev) => new Set(prev).add(midi));
      } else if (msg.type === "off") {
        setHeld((prev) => {
          if (!prev.has(midi)) return prev;
          const next = new Set(prev);
          next.delete(midi);
          return next;
        });
      }
    };

    const wireInputs = () => {
      if (!access) return;
      const inputs = Array.from(access.inputs.values());
      inputs.forEach((input) => {
        input.onmidimessage = handleMessage;
      });
      if (!cancelled) {
        setDevices(inputs.length);
        setDeviceNames(inputs.map((i) => i.name || "MIDI input"));
        setStatus(inputs.length ? "connected" : "no-devices");
      }
    };

    navigator
      .requestMIDIAccess()
      .then((midiAccess) => {
        if (cancelled) return;
        access = midiAccess;
        access.onstatechange = wireInputs; // hot-plug devices
        wireInputs();
      })
      .catch(() => {
        if (!cancelled) setStatus("denied");
      });

    return () => {
      cancelled = true;
      if (access) {
        access.onstatechange = null;
        Array.from(access.inputs.values()).forEach((input) => {
          input.onmidimessage = null;
        });
      }
    };
  }, [enabled]);

  return { status, devices, deviceNames, held, seen, last, reset };
}

export function useMidiPresence() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!midiSupported()) return undefined;
    let access = null;
    let cancelled = false;
    const update = () => {
      if (access && !cancelled) setConnected(access.inputs.size > 0);
    };
    navigator
      .requestMIDIAccess()
      .then((a) => {
        if (cancelled) return;
        access = a;
        access.addEventListener("statechange", update);
        update();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (access) access.removeEventListener("statechange", update);
    };
  }, []);

  return connected;
}
